import React from 'react';
import { Box, Typography, Card, CardContent, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Title, useTranslate } from 'react-admin';
import { ReportExportButtons, ReportExportButtonsProps } from './ReportExportButtons';

export interface ReportLayoutProps {
  title: string;
  description?: string;
  filters?: React.ReactNode;
  exportButtons?: ReportExportButtonsProps;
  loading?: boolean;
  error?: Error | null;
  children: React.ReactNode;
}

export const ReportLayout: React.FC<ReportLayoutProps> = ({
  title,
  description,
  filters,
  exportButtons,
  loading = false,
  error = null,
  children,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <Title title={title} />
      <Box sx={{ backgroundColor: 'transparent', minHeight: 'calc(100vh - 120px)' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            mb: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body1" sx={{ color: '#606060' }}>
                {description}
              </Typography>
            )}
          </Box>

          {/* Export Buttons */}
          {exportButtons && (
            <ReportExportButtons {...exportButtons} />
          )}
        </Box>

        {/* Filters Section */}
        {filters && (
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {filters}
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {translate('reports.errors.loadFailed', { fallback: 'Failed to load report data' })}: {error.message}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress size={60} sx={{ color: '#009A75' }} />
          </Box>
        )}

        {/* Content */}
        {!loading && !error && children}
      </Box>
    </>
  );
};
